import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ContactSection = styled.section`
  padding: 5rem 0;
  background: ${props => props.theme.colors.backgroundDark};
  color: white;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-family: ${props => props.theme.fonts.script};
    font-size: 3rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
`;

const ContactCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-10px);
  }
`;

const ContactIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  opacity: 0.9;
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
  margin-bottom: 1rem;
`;

const ContactInfo = styled.div`
  margin-bottom: 1.5rem;
  
  p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 0.5rem;
    line-height: 1.6;
    
    a {
      color: ${props => props.theme.colors.primary};
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
  
  .highlight {
    color: white;
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const ContactButton = styled.a`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.secondary};
    transform: translateY(-2px);
  }
`;

const BusinessHours = styled(motion.div)`
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin-top: 3rem;
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  
  h4 {
    font-size: 1.5rem;
    font-weight: 600;
    color: white;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
    font-weight: 500;
  }
`;

const CallToAction = styled(motion.div)`
  text-align: center;
  margin-top: 4rem;
  padding: 3rem 2rem;
  background: rgba(46, 204, 113, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(46, 204, 113, 0.3);
  
  h3 {
    font-size: 2rem;
    color: white;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(45deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  text-decoration: none;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(46, 204, 113, 0.6);
  }
  
  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
    margin-bottom: 0.5rem;
  }
`;

const ContactPreview = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  return (
    <ContactSection ref={ref}>
      <Container>
        <SectionHeader>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Kontakt & Anfahrt
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Haben Sie Fragen oder möchten Sie uns besuchen? 
            Wir freuen uns auf Ihre Nachricht und heißen Sie herzlich willkommen!
          </motion.p>
        </SectionHeader>

        <ContactGrid>
          <ContactCard
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ContactIcon>📞</ContactIcon>
            <ContactTitle>Telefon</ContactTitle>
            <ContactInfo>
              <p className="highlight">{process.env.REACT_APP_CONTACT_PHONE || '0162 1353794'}</p>
              <p>
                Rufen Sie uns gerne an für Fragen, 
                Terminvereinbarungen oder ein 
                unverbindliches Beratungsgespräch.
              </p>
            </ContactInfo>
            <ContactButton href={`tel:${(process.env.REACT_APP_CONTACT_PHONE || '01621353794').replace(/\s/g, '')}`}>
              Jetzt anrufen
            </ContactButton>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <ContactIcon>✉️</ContactIcon>
            <ContactTitle>E-Mail</ContactTitle>
            <ContactInfo>
              <p className="highlight">
                <a href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'derponystall@gmx.de'}`}>
                  {process.env.REACT_APP_CONTACT_EMAIL || 'derponystall@gmx.de'}
                </a>
              </p>
              <p>
                Schreiben Sie uns eine E-Mail mit Ihren 
                Fragen oder Buchungswünschen. 
                Wir antworten schnellstmöglich.
              </p>
            </ContactInfo>
            <ContactButton href={`mailto:${process.env.REACT_APP_CONTACT_EMAIL || 'derponystall@gmx.de'}`}>
              E-Mail schreiben
            </ContactButton>
          </ContactCard>

          <ContactCard
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <ContactIcon>📍</ContactIcon>
            <ContactTitle>Adresse</ContactTitle>
            <ContactInfo>
              <p className="highlight">Der Ponystall</p>
              <p>Marcella Deeken</p>
              <p>
                Weidenweg 2<br />
                26683 Saterland
              </p>
              <p>
                Besuchen Sie uns gerne vor Ort! 
              </p>
            </ContactInfo>
            <ContactButton href="https://www.google.com/maps/search/?api=1&query=Weidenweg+2,+26683+Saterland" target="_blank" rel="noopener noreferrer">
              Anfahrt planen
            </ContactButton>
          </ContactCard>
        </ContactGrid>

        <BusinessHours
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <h4>📅 Öffnungszeiten</h4>
          <p>Montag bis Freitag</p>
        </BusinessHours>

        <CallToAction
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <h3>Bereit für Ihr erstes Pony-Erlebnis?</h3>
          <p>
            Buchen Sie direkt online eine Schnupperstunde oder kontaktieren Sie uns 
            für weitere Informationen. Wir freuen uns darauf, Sie kennenzulernen!
          </p>
          <CTAButtons>
            <CTAButton to="/kurse">
              Online buchen
            </CTAButton>
            <CTAButton to="/kurse" style={{background: 'rgba(255,255,255,0.1)', border: '2px solid #2ecc71'}}>
              Kurse entdecken
            </CTAButton>
          </CTAButtons>
        </CallToAction>
      </Container>
    </ContactSection>
  );
};

export default ContactPreview;